<template>
  <div class="max-w-lg w-full mx-auto px-4 flex flex-col">
    <!-- Progress Bar -->
    <div
      v-if="isMultiStep"
      class="w-full bg-gray-200 rounded-full h-1.5 order-first absolute top-0 left-0"
    >
      <div class="bg-blue-500 h-1.5" :style="{ width: progress + '%' }"></div>
    </div>

    <!-- QUB Logo -->
    <div v-if="organiserSlug === 'qub'" class="justify-center">
      <img
        class="mx-auto h-50"
        src="https://imgs.search.brave.com/cUc6ZH6FjB78kcNkN6l4QEioNddtZ7K55mnHlOk4sHE/rs:fit:860:0:0/g:ce/aHR0cHM6Ly9pLnBp/bmltZy5jb20vb3JpZ2lu/YWxzL2Y1L2ZmL2U4/L2Y1ZmZlODliYjRl/NTFlNDZiZjQ4OTQ2/NWJkNzJkOGVkLmpw/Zw"
        width="25%"
        alt="QUB Logo"
      />
    </div>
    <!-- Single Step Form -->
    <div v-if="!isMultiStep">
      <FormSection
        :form="form"
        ref="formSection"
        :events="events"
        :additional-fields="applyConditions()"
        :hideEvent="hideEvent"
        :submit-disabled="submitting"
        @submit="submit"
        @graduation-date-change="onGraduationDateChange"
      />
    </div>

    <!-- Multi Step Form -->
    <div v-else>
      <!-- Multi Step Form -->
      <div v-if="isMultiStep">
        <!-- Step 1: Your Details -->
        <div v-if="currentStep === 1">
          <FormSection
            :form="form"
            ref="formSection"
            :events="events"
            :additional-fields="[]"
            :hideEvent="hideEvent"
            @submit="nextStep"
            submit-button-text="Continue"
          />
        </div>

        <!-- Step 2: Graduation Details -->
        <div v-if="currentStep === 2">
          <button
            type="button"
            v-if="forceStep < 2"
            @click="previousStep"
            class="mt-4 mb-4 text-blue-500 hover:text-blue-700 flex items-center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 mr-1"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                clip-rule="evenodd"
              />
            </svg>
            Back
          </button>
          <FormSection
            :form="form"
            :events="events"
            :additional-fields="applyConditions()"
            :hideEvent="hideEvent"
            :submit-disabled="submitting"
            @submit="submit"
            submit-button-text="Continue"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { extend } from "vee-validate";
import {
  required,
  min,
  max,
  email,
  regex,
  confirmed,
} from "vee-validate/dist/rules";
import FormSection from "./FormSection.vue";

// Validation rules
extend("personal_email", {
  message: (field) =>
    "The " +
    field +
    " value is not a personal email, please enter an email not ending in ac.uk.",
  validate: (value) => !/^.*((\.ac\.uk)|(bpp\.com))$/.test(value),
});

extend("required", {
  ...required,
  message: "This field is required",
});
extend("min", min);
extend("confirmed", {
  ...confirmed,
  message: "Your email addresses do not match",
});
extend("regex", regex);
extend("max", max);
extend("email", email);

export default {
  name: "RegistrationForm",
  components: {
    FormSection,
  },
  props: {
    events: {
      type: Array,
      required: true,
    },
    additionalFieldsInput: {
      type: Array,
      required: true,
    },
    name: {
      type: String,
      default: "",
    },
    event: {
      type: String,
      default: "",
    },
    email: {
      type: String,
      default: "",
    },
    organiserSlug: {
      type: String,
      required: true,
    },
    forceStep: {
      type: Number,
      default: 1,
    },
    hideEvent: {
      type: Boolean,
      default: false,
    },
    submitting: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    forceStep(newVal) {
      this.currentStep = newVal;
      this.progress = newVal === 2 ? 66 : 33;
    },
  },
  data() {
    return {
      currentStep: 1,
      progress: 33,
      form: {
        name: this.name || "",
        event: this.event || null,
        email: this.email || "",
        confirmEmail: this.email || "",
        additionalFields: [],
      },
    };
  },
  computed: {
    isMultiStep() {
      return process.env.VUE_APP_FORM_MODE === "multi";
    },
  },
  methods: {
    nextStep() {
      if (this.currentStep < 2) {
        this.currentStep++;
        this.progress = Math.min(this.progress + 33, 100); // Update progress
      }
    },
    previousStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
        this.progress = Math.max(this.progress - 33, 33); // Update progress
      }
    },
    onGraduationDateChange(eventId) {
      if (!eventId) {
        console.warn("No valid graduation date selected.");
      } else {
        console.log("Valid graduation date selected:", eventId);
      }
    },
    resetForm() {
      this.currentStep = 1;
      this.progress = 33;
      this.form = {
        name: "",
        email: "",
        confirmEmail: "",
        event: null,
        additionalFields: [],
      };
    },
    initializeAdditionalFields(fields) {
      fields.forEach((field) => {
        let existingField = this.form.additionalFields.find(
          (f) => f.name === field.name
        );
        if (!existingField) {
          this.form.additionalFields.push({
            ...field,
            ...(field.type === "multi-select" && { value: [] }),
          });
        }
      });
    },

    submit() {
      if (this.submitting) {
        return;
      }
      this.$emit("submit", this.form);
    },
    getField(field) {
      return this.form.additionalFields.find((f) => f.name === field.name);
    },
    applyConditions() {
      // Use JSON parse/stringify for deep cloning
      const output_fields = JSON.parse(
        JSON.stringify(this.additionalFieldsInput)
      );
      for (let field_index in output_fields) {
        let field = output_fields[field_index];
        if (!("enabled" in field)) {
          field["enabled"] = true;
        }
        if (!("display" in field)) {
          field["display"] = true;
        }
        if ("conditions" in field) {
          for (let condition_index in field.conditions) {
            let condition = field.conditions[condition_index];
            var value_to_check;
            var possible_fields = this.form.additionalFields.filter(
              (element) => {
                return element.name == condition.field;
              }
            );
            if (condition.comparator == "equals") {
              if (possible_fields.length == 1) {
                value_to_check = possible_fields[0].value;
              } else {
                value_to_check = this.form[condition.field];
              }
              console.log(value_to_check);
              if (value_to_check == condition.value) {
                console.log(condition);
                switch (condition.operation.type) {
                  case "show":
                    field[condition.operation.relates_to] = field[
                      condition.operation.relates_to
                    ].filter((element, index) => {
                      console.log(element);
                      return condition.operation.indexes.includes(index);
                    });
                    break;
                  case "enabled":
                    field[condition.operation.relates_to] =
                      condition.operation.enabled;
                    break;
                }
              }
            } else if (condition.comparator == "property_equals") {
  try {
    const controllingFieldName = condition.field; // e.g. "faculty"

    // Already computed earlier in applyConditions:
    // var possible_fields = this.form.additionalFields.filter(
    //   (element) => element.name == condition.field
    // );

    const controllingFormField =
      possible_fields.length === 1
        ? possible_fields[0]                 // additional field (like "faculty")
        : { value: this.form[controllingFieldName] }; // fallback for top-level fields

    const selectedValue = controllingFormField
      ? controllingFormField.value          // e.g. "Newcastle University Business School"
      : undefined;

    // Find the controlling field DEFINITION in the cloned + conditioned fields
    const controllingFieldDef = output_fields.find(
      (f) => f.name === controllingFieldName
    );

    if (!controllingFieldDef || !Array.isArray(controllingFieldDef.choices)) {
      console.warn("No controlling field/choices for", controllingFieldName);
      break;
    }

    // Find the choice object the user picked in that field
    const selectedChoice = controllingFieldDef.choices.find(
      (choice) => choice.value === selectedValue
    );

    if (!selectedChoice) {
      console.warn(
        `No choice found for value "${selectedValue}" on field "${controllingFieldName}"`
      );
      break;
    }

    // 🔴 THIS is where `unique` is actually used
    // condition.property === "unique", so this is selectedChoice.unique
    value_to_check = selectedChoice[condition.property];
    console.log(
      `property_equals: ${controllingFieldName}.${condition.property} =`,
      value_to_check
    );
  } catch (e) {
    console.error("Caught an error in property_equals:", e);
    break;
  }

  if (value_to_check == condition.value) {
    console.log(condition);
    switch (condition.operation.type) {
      case "show":
        field[condition.operation.relates_to] = field[
          condition.operation.relates_to
        ].filter((element, index) => {
          console.log(element);
          return condition.operation.indexes.includes(index);
        });
        break;
      case "enabled":
        field[condition.operation.relates_to] = condition.operation.enabled;
        break;
    }
  }
}          }
        }
      }
      console.log(output_fields);
      return output_fields.filter((i) => i.display);
    },
  },
  created() {
    this.currentStep = this.forceStep;
    // Initialize form
    this.initializeAdditionalFields(this.additionalFieldsInput);
    this.form.additionalFields.forEach((item) => {
      if (item.regex != null) {
        item.regex = new RegExp(item.regex);
      }
    });
  },
};
</script>

<style scoped>
.markdownHtmlCustom a {
  text-decoration: underline !important;
}
</style>
